<template>
  <page-component id="weblotteryenrollment" :header="`${lotterydescription} - Lottery Enrollment`">
    <div id="weblotteryenrollment-info" v-if="lotterycomment" >
      <p id="weblotteryenrollment-info-title">Lottery Information</p>
      <p id="weblotteryenrollment-info-comment" v-html="lotterycomment"></p>
    </div>

    <form ref="form" id="weblotteryenrollment-form" class="novalidate" @submit.prevent="onSubmitAll()">
      <input type="hidden" name="Action" value="process" />
      <input type="hidden" name="SubAction" value="" />
      <input type="hidden" name="_csrf_token" :value="csrf" />
      <input type="hidden" name="LotteryOption" :value="lotteryoption" />
      <input type="hidden" name="LotteryGroup" :value="lotterygroup" />
      <input type="hidden" name="LotteryCards" :value="cards.length" />

      <group-component header="Step 1 - Select Participating Family Members">
        <div id="weblotteryenrollment-persons">
          <checkbox-component v-for="(person) in persons" :key="person.personId"
            :id="'weblotteryenrollment-person' + person.personId"
            :label="person.firstName + ' ' + person.lastName"
            :formInputName="'weblotteryenrollment-person' + person.personId" 
            v-model=person.selected
            @change="onPersonClick($event, person)"
            :disabled="processing || person.existing"         
            labelRight
            >
          </checkbox-component>
        </div>   
        <div id="weblotteryenrollment-persons-existing" v-if="personsExisting">
          <div class="icon">
            <span class="secondary">
              <svgicon icon="glyph/16/info" width="16" height="16"></svgicon>
            </span>
          </div>
          <p>Family members with an asterisk(*) are already enrolled in this lottery. Please visit the <a :href="'pendingenrollments.html?_csrf_token=' + csrf"> My Pending Enrollments</a> page to update any preexisting enrollments.</p>
        </div>               
      </group-component> 

      <group-component header="Step 2 - Select a Lottery Enrollment Option">
        <div id="weblotteryenrollment-lotteryoption">
          <button
            id="weblotteryenrollment-individual"
            type="button"
            :class="['button', {secondary: lotteryoption === 'individual' }]"
            :disabled="processing"
            @click.prevent="onIndividual()"
            >
            Individual Lottery Enrollment
          </button>  
          <button
            id="weblotteryenrollment-linked"
            type="button"
            :class="['button', {secondary: lotteryoption === 'linked' }]"
            :disabled="processing || personsSelected <= 1"
            @click.prevent="onLinked()"
            >
            Linked Lottery Enrollments
          </button>      
        </div>
        <div id="weblotteryenrollment-lotteryoptiontext">
          <div class="icon">
            <span class="secondary">
              <svgicon icon="glyph/16/info" width="16" height="16"></svgicon>
            </span>
          </div>
          <p>{{lotteryoptiontext}}</p>
        </div>   
        <div id="weblotteryenrollment-lotteryoptionlink">
          <a id="weblotteryenrollment-learn" :href="baseHref + 'splash.html?ccode=LotteryEnrollmentOptionsHelp&type=Lottery'" target="_blank">Learn more about Lottery Enrollment Options</a>
        </div>   
      </group-component> 

      <group-component header="Step 3 - Select Enrollment Choices">
        <div id="weblotteryenrollment-card" v-for="(card) in cards" :key="card.cardnumber">
          <input type="hidden" :name="'weblotteryenrollment-card-choices-' + card.cardnumber"  :value="card.choices.length" />
          <p>{{card.title}}</p>
          <div v-for="(choice, index) in card.choices" :key="choice.label">
            <input type="hidden" :name="'weblotteryenrollment-card-person-' + card.cardnumber + '-' + (index + 1)"  :value="choice.id" />
            <input type="hidden" :name="'weblotteryenrollment-card-section-' + card.cardnumber + '-' + (index + 1)"  :value="choice.value" />
            <lottery-choice-component
              :id="'weblotteryenrollment-card-section-' + card.cardnumber + '-' + (index + 1)" 
              :ref="card.cardnumber + '-' + (index + 1)"
              :allActivities="activities"
              :label=choice.label
              :personId="card.personId"
              :initialFilters="[{name:'age',value:choice.person.age},{name:'gender',value:choice.person.gender},{name:'grade',value:choice.person.grade}]"
              :lotterygroup="`${lotterygroup}`"
              v-model=choice.value
              type="text"
              newline>
            </lottery-choice-component>
          </div> 
          <button v-if="lotteryoption === 'individual' && card.choices.length < numberofchoices"
            :id="'weblotteryenrollment-addchoice-' + card.cardnumber"
            type="button"
            class=""
            :disabled="processing"
            @click.prevent="onAddChoice(card)"
            >
            + Add Another Choice
          </button>   
        </div>  
        <div id="weblotteryenrollment-card" v-if="lotteryoption === 'linked' && this.cards.length < numberofchoices">
          <button
            id="weblotteryenrollment-addlinkedchoice"
            type="button"
            class=""
            :disabled="processing"
            @click.prevent="onAddLinkedChoice()"
            >
            + Add Another Choice
          </button>  
        </div>         
      </group-component>       

      <group-component header="Step 4 - Confirm Selections and Submit">
        <button
          id="weblotteryenrollment-submit"
          type="button"
          class="button primary right"
          :disabled="processing"
          @click.prevent="onSubmitAll()"
          >
          Submit Lottery Selections
        </button>   
      </group-component> 
    </form> 
  </page-component>
</template>

<script>
import LotteryEnrollmentServices from "@/services/LotteryEnrollmentServices"
import { mutations } from "@/store/types";

export default {
  name: "LotteryEnrollment",
  props: {
    lotterygroup: String,
    lotterycomment: String,
    lotterydescription: String,
    fmid: Number,
    numberofchoices: Number,
    persons: Array,
    activities: Array,
  },
  data() {
    return {
      csrf: App.settings.CSRFToken,
      baseHref: App.settings.BaseHref,
      processing: false,
      cards: [],
      personsSelected: 0,
      personsExisting: false,
      lotteryoption: "individual",
      lotteryoptiontext: "Each participating Family Member's lottery enrollment choices are prioritized individually; Family Member selections do not affect each other."
    }
  },
  created() { 
    this.persons.forEach(person => {
      if ( person.existing === true ) {
        this.personsExisting = true;
        return;
      }
    })
  },  
  methods: {
    async onIndividual() {
      if (this.lotteryoption === "individual") return;
      this.lotteryoption = "individual";
      this.lotteryoptiontext = "Each participating Family Member's lottery enrollment choices are prioritized individually; Family Member selections do not affect each other.";
      
      let tempcards = this.cards;
      let tempchoices = [];
      this.cards = [];
    
      this.persons.forEach(person => {
        if ( person.selected === true  && person.existing === false ) {
          tempchoices = [];
          tempcards.forEach(card => {
            card.choices.forEach(choice => {
              if ( choice.id === person.personId ) {
                choice.label = "Choice " + card.cardnumber;
                choice.person = person;
                tempchoices.push(choice);
              }
            }) 
          }) 
          this.cards.push({cardnumber: this.cards.length + 1, personId: person.personId, title: person.firstName, person:person, choices: tempchoices});
        }
      })
    },
   
    async onLinked() {
      if (this.lotteryoption === "linked") return;
      this.lotteryoption = "linked";
      this.lotteryoptiontext = "Choose this option to create stable lottery enrollment scenarios that work for your Household as a whole. Each Family Member's lottery enrollment choices are prioritized by choice number. Any one individual will only get their 1st Choice if all Family Members in the Household can get their 1st Choice. Repeat for 2nd Choice and beyond.";
    
      let tempcards = this.cards;
      let tempnumberofcards = 0;
      let tempchoices = [];
      this.cards = [];

      tempcards.forEach(card => {
        if ( card.choices && card.choices.length > tempnumberofcards ) {
          tempnumberofcards = card.choices.length;
        }
      }) 
 
      for (let i = 0; i < tempnumberofcards; i++) {
        tempchoices = [];

        tempcards.forEach(card => {
          if ( card.choices[i] ) {
            card.choices[i].label = card.title;
            tempchoices.push(card.choices[i]);
          }
          else {
            tempchoices.push({label: card.title, value: "", id: card.personId, person:card.person});
          }
        }) 

        this.cards.push({cardnumber: this.cards.length + 1, personId: 0, title: "Choice " + (this.cards.length + 1), choices: tempchoices});
      }

    },   

    async onPersonClick(e, person) {
      if (e) {
        this.personsSelected++;

        this.$set(person, "selected", true);

        if (this.lotteryoption === "individual") {
          if (this.numberofchoices === 1) {
            this.cards.push({cardnumber: this.cards.length + 1, personId: person.personId, person:person, title: person.firstName, 
              choices: [{label: "Choice 1", value: "", id: person.personId, person:person}]});
          }
          else {
            this.cards.push({cardnumber: this.cards.length + 1, personId: person.personId, person:person, title: person.firstName, 
              choices: [{label: "Choice 1", value: "", id: person.personId, person:person}, {label: "Choice 2", value: "", id: person.personId, person:person}]});
          }
        }
        else {
          this.cards.forEach(card => {
            card.choices.push({label: person.firstName, value: "", id: person.personId, person:person});
          })  
        }

        if (this.personsSelected === 2 && this.lotteryoption === "individual") {
          this.onLinked();
        }
      }
      else {
        this.personsSelected--;

        this.$set(person, "selected", false);

        if (this.personsSelected === 1 && this.lotteryoption === "linked") {
          this.onIndividual();
          return;
        }

        if (this.lotteryoption === "individual") {
          this.cards = this.cards.filter((p)=>p.personId !== person.personId);
        }
        else {
          this.cards.forEach(card => {
            card.choices = card.choices.filter((p)=>p.id !== person.personId);
          })   
        }        
      }
    },    

    async onAddChoice(card) {
      card.choices.push({label: "Choice " + (card.choices.length + 1), value: "", id: card.personId, person:card.person});
    },

    async onAddLinkedChoice() {
      this.cards.push({cardnumber: this.cards.length + 1, personId: 0, title: "Choice " + (this.cards.length + 1), choices: []});

      this.persons.forEach(person => {
        if ( person.selected === true && person.existing === false) {
          this.cards.forEach(card => {
            if (card.cardnumber === this.cards.length) {
              card.choices.push({label: person.firstName, value: "", id: person.personId, person:person});
            }
          })             
        }
      })
    },

    async onSubmitAll() {
      this.processing = true;

      let personcheck = false;

      this.persons.forEach(person => {
        if ( person.selected === true  && person.existing === false) {
          personcheck = true;
          return;
        }
      })

      if ( !personcheck ) {
        this.$store.commit(mutations.ADD_MESSAGE, {
          type: "error", 
          text: "Please select at least one Family Member to submit this lottery enrollment."
        });    
        this.processing = false;
        return;
      }
      
      let cardcheck = true;

      if (this.lotteryoption === "individual") {
        this.cards.forEach(card => {
          card.choices.forEach(choice => {
            if ( choice.label === "Choice 1" && choice.value === "") {
              cardcheck = false;
              return;
            }
          })
        })
      }
      else {
        this.cards.forEach(card => {
          if ( card.cardnumber === 1 ) {
            card.choices.forEach(choice => {
              if ( choice.value === "") {
                cardcheck = false;
                return;
              }
            })
          }
        })
      }

      if ( !cardcheck ) {
        this.$store.commit(mutations.ADD_MESSAGE, {
          type: "error", 
          text: "Please select all first choice Activity Section(s) for all Family Members participating in this lottery."
        });
        this.processing = false;        
        return;
      }          

      this.submitForm();
    },

    submitForm() {

      const formData = new FormData(this.$refs.form);
      const formParams = {};

      for (let key of formData.keys()) {
        formParams[key] = formData.get(key);
      }

      LotteryEnrollmentServices
        .processEnrollments( formParams )
        .then((response) => {
          this.processing = false;

          if (response.success) {
            window.location.replace(App.settings.BaseHref + "pendingenrollments.html?displaysuccess=yes&lotterygroup=" + this.lotterygroup);
            return;  
          }

          this.$store.commit(mutations.ADD_MESSAGE, {
            type: "error",
            text: response.error || 'Unable to perform lottery enrollment, please try again later.',
          });
        })
        .catch(() => {
          this.processing = false;
          this.$store.commit(mutations.ADD_MESSAGE, {
            type: "error",
            text: `Unable to perform lottery enrollment, please try again later.`,
          });
        }) 

    }    
  }
}
</script>

<style lang="less" scoped>
@import "../../assets/css/abstract/variables";
@import "../../assets/css/abstract/mixins";

#weblotteryenrollment {
  p {
    margin: 0;
    padding: 0;
  }

  #weblotteryenrollment-info {
    background: @gray-color;
    border-radius: 4px;
    border: 1px solid rgba(192, 192, 192, 0.5);
    padding: 12px;
    margin-top: 16px;
  }

  #weblotteryenrollment-info-title {
    font-weight: 600;
    margin-bottom: 10px;
  }

  // #weblotteryenrollment-persons .checkbox-wrap {
  //   width: auto;
  //   float: left;
  //   padding-right: 50px;
  // }

  #weblotteryenrollment-persons .checkbox-wrap--state-disabled {
    opacity: .7;
  }

  #weblotteryenrollment-lotteryoption {
    width: 100%;
    margin-top: 5px;
    text-align: center;
    float: left;

    button {
      float: none;
    }
  }

  #weblotteryenrollment-lotteryoptiontext, #weblotteryenrollment-persons-existing {
    width: 100%;
    margin-top: 20px;
    padding-left: 40px;
    float: left;
    position: relative;

    .icon {
      position: absolute;
      top: 2px;
      left: 5px;
      width: 16px;
      height: 16px;

      .svg-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        margin-top: -8px;
        margin-left: -8px;
      }
    }

  }

  #weblotteryenrollment-lotteryoptionlink {
    width: 100%;
    margin-top: 20px;
    float: left;
    text-align: right;
  }

  #weblotteryenrollment-card {
    width: 23%;
    margin: 0 1% 16px 1%;
    text-align: center;
    float: left;
    background: @gray-color;
    border-radius: 4px;
    border: 1px solid rgba(192, 192, 192, 0.5);

    p {
      text-align: center;
      font-size: @font-size-16px;
      font-weight: 600;
      padding: 6px 0;
      border-bottom: 1px solid rgba(192, 192, 192, 0.5);
    }

    .fillin-wrap {
      padding: 12px;

      label {
        width: 100%;
      }  

    }

    button {
      margin-bottom: 12px;
      color: @primary-color;
      font-weight: 600;
      font-size: @font-size-14px;
    }

    button:hover {
      text-decoration: underline;
    }

  }

  #weblotteryenrollment-addlinkedchoice {
    margin-top: 12px;
  }

}

@media @tablet-down { 

  #weblotteryenrollment-card {
    width: 100% !important;
    margin: 0 0 16px 0 !important;
  }  

}
</style>